/**
 * SharePage - Photo sharing page for Bulaccino Cafe Viral Marketing System
 * Accessed via QR code scan at tables or direct link
 * Mobile-first design for easy in-cafe use
 */
import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Camera, Image as ImageIcon, Coffee, UtensilsCrossed, Cake, Salad, 
  Sandwich, MapPin, Facebook, Instagram, Star, MessageCircle,
  CheckCircle, Copy, User, Mail, Phone, ArrowRight, Sparkles,
  X, Upload, Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Helmet } from 'react-helmet-async';
import { toast } from 'sonner';
import api from '@/utils/apiClient';

// Item type options
const ITEM_TYPES = [
  { id: 'coffee', icon: Coffee, label: 'Coffee' },
  { id: 'cold_drink', icon: Coffee, label: 'Cold Drink' },
  { id: 'pastry', icon: Cake, label: 'Pastry' },
  { id: 'breakfast', icon: UtensilsCrossed, label: 'Breakfast' },
  { id: 'lunch', icon: Salad, label: 'Lunch' },
  { id: 'dessert', icon: Cake, label: 'Dessert' },
  { id: 'sandwich', icon: Sandwich, label: 'Sandwich' },
  { id: 'cafe', icon: MapPin, label: 'Cafe View' }
];

// Platform configuration with points and share templates
const PLATFORMS = [
  { 
    id: 'facebook', 
    name: 'Facebook', 
    icon: Facebook, 
    points: 25, 
    color: 'bg-blue-600', 
    limit: '1/day',
    shareTemplate: "Just enjoyed the most amazing coffee at Bulaccino Cafe in Fiji! ☕ Seriously the best cafe I've found here. If you're visiting Nadi, this is a MUST! #BulaccinoCafe #FijiCoffee #NadiEats"
  },
  { 
    id: 'instagram', 
    name: 'Instagram', 
    icon: Instagram, 
    points: 25, 
    color: 'bg-gradient-to-br from-purple-600 to-pink-500', 
    limit: '1/day',
    shareTemplate: "Found my new favorite spot in Fiji! 🌴☕ The vibes at @bulaccinocafe are unmatched. Fresh coffee, amazing food, and the friendliest staff!\n\n#BulaccinoCafe #FijiCoffee #NadiFood #FijiEats #CoffeeLovers #TravelFiji #IslandLife"
  },
  { 
    id: 'google', 
    name: 'Google', 
    icon: Star, 
    points: 50, 
    color: 'bg-red-500', 
    limit: 'Once',
    shareTemplate: "Absolutely loved Bulaccino Cafe! Best coffee in Nadi, Fiji. The atmosphere is warm and welcoming, the food is fresh and delicious, and the staff treats you like family. A must-visit for any coffee lover traveling to Fiji. Will definitely be back! Highly recommend checking their menu at bulaccinocafe.com"
  },
  { 
    id: 'tripadvisor', 
    name: 'TripAdvisor', 
    icon: MapPin, 
    points: 50, 
    color: 'bg-green-600', 
    limit: 'Once',
    shareTemplate: "If you're looking for the best cafe in Fiji, look no further! Bulaccino Cafe exceeded all my expectations. Great coffee, wonderful breakfast options, and such a cozy atmosphere. The staff made us feel right at home. Perfect for a quick coffee break or a leisurely meal. Don't miss their Bula Coffee Club - amazing value! Visit bulaccinocafe.com"
  },
  { 
    id: 'whatsapp', 
    name: 'WhatsApp', 
    icon: MessageCircle, 
    points: 15, 
    color: 'bg-emerald-500', 
    limit: '3/day',
    shareTemplate: "Hey! Just discovered this amazing cafe in Fiji - Bulaccino Cafe! ☕🌴 The coffee is incredible and the food is so fresh. You HAVE to check it out if you're ever in Nadi! Here's their menu: bulaccinocafe.com"
  }
];

// Platform Button Component - Mobile optimized (icons only on mobile)
const PlatformButton = ({ platform, selected, onClick, disabled }) => {
  const Icon = platform.icon;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`
        relative flex flex-col items-center p-2 sm:p-3 rounded-xl border-2 transition-all
        ${selected 
          ? 'border-primary bg-primary/10 scale-105' 
          : 'border-border hover:border-primary/50 bg-card'
        }
        ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
      `}
    >
      <div className={`w-12 h-12 sm:w-10 sm:h-10 rounded-full ${platform.color} flex items-center justify-center mb-1 sm:mb-2`}>
        <Icon className="w-6 h-6 sm:w-5 sm:h-5 text-white" />
      </div>
      {/* Hide platform name on mobile, show only on sm+ screens */}
      <span className="hidden sm:block text-xs font-medium text-foreground">{platform.name}</span>
      {/* Points always visible */}
      <span className="text-xs sm:text-[10px] text-primary font-bold">+{platform.points}</span>
      {selected && (
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          className="absolute -top-1 -right-1 w-5 h-5 bg-primary rounded-full flex items-center justify-center"
        >
          <CheckCircle className="w-3 h-3 text-white" />
        </motion.div>
      )}
    </button>
  );
};

// Item Type Button Component  
const ItemTypeButton = ({ item, selected, onClick }) => {
  const Icon = item.icon;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`
        flex flex-col items-center p-2 rounded-lg border transition-all text-xs
        ${selected 
          ? 'border-primary bg-primary/10 text-primary' 
          : 'border-border hover:border-primary/50 text-muted-foreground'
        }
      `}
    >
      <Icon className="w-4 h-4 mb-1" />
      <span>{item.label}</span>
    </button>
  );
};

export default function SharePage() {
  const [searchParams] = useSearchParams();
  const phoneFromUrl = searchParams.get('phone');
  const emailFromUrl = searchParams.get('email');
  
  // User state
  const [user, setUser] = useState(null);
  const [checkingUser, setCheckingUser] = useState(true);
  const [showSignup, setShowSignup] = useState(false);
  
  // Share form state
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [selectedItemType, setSelectedItemType] = useState(null);
  const [caption, setCaption] = useState('');
  const [photoPreview, setPhotoPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [shareSuccess, setShareSuccess] = useState(false);
  
  // Signup form state
  const [signupData, setSignupData] = useState({
    name: '',
    email: emailFromUrl || '',
    phone: phoneFromUrl || ''
  });
  
  const fileInputRef = useRef(null);
  
  // Record QR scan on page load
  useEffect(() => {
    const recordScan = async () => {
      try {
        await api.post('/viral/qr/scan?qr_type=share');
      } catch (err) {
        console.log('QR scan tracking failed:', err);
      }
    };
    recordScan();
  }, []);
  
  // Check if user exists
  useEffect(() => {
    const checkUser = async () => {
      if (!phoneFromUrl && !emailFromUrl) {
        setCheckingUser(false);
        return;
      }
      
      try {
        const params = new URLSearchParams();
        if (phoneFromUrl) params.append('phone', phoneFromUrl);
        if (emailFromUrl) params.append('email', emailFromUrl);
        
        const res = await api.get(`/viral/check-user?${params}`);
        if (res.data.exists) {
          setUser(res.data);
        }
      } catch (err) {
        console.error('Failed to check user:', err);
      }
      setCheckingUser(false);
    };
    
    checkUser();
  }, [phoneFromUrl, emailFromUrl]);
  
  // Handle photo selection
  const handlePhotoSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Photo must be less than 10MB');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };
  
  // Handle signup
  const handleSignup = async (e) => {
    e.preventDefault();
    
    if (!signupData.name || !signupData.email || !signupData.phone) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setSubmitting(true);
    try {
      const res = await api.post('/viral/account/create', {
        name: signupData.name,
        email: signupData.email,
        phone: signupData.phone
      });
      
      const data = res.data;
      
      if (data.success) {
        setUser({
          exists: true,
          user_id: data.user_id,
          name: signupData.name,
          total_points: data.total_points || 0
        });
        setShowSignup(false);
        toast.success(data.message || 'Account created! You earned 50 points!');
      } else {
        toast.error(data.detail || 'Failed to create account');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create account');
    }
    setSubmitting(false);
  };
  
  // Handle share submission
  const handleShare = async () => {
    if (!selectedPlatform) {
      toast.error('Please select a platform to share on');
      return;
    }
    
    setSubmitting(true);
    try {
      const payload = {
        platform: selectedPlatform,
        item_type: selectedItemType,
        caption: caption,
        phone: phoneFromUrl || signupData.phone,
        email: emailFromUrl || signupData.email
      };
      
      const res = await api.post('/viral/share/submit', payload);
      const data = res.data;
      
      if (data.success) {
        setShareSuccess(true);
        
        if (data.requires_account) {
          setShowSignup(true);
          toast.info(`Create an account to claim your ${data.potential_points} points!`);
        } else {
          toast.success(data.message);
          // Update user points if available
          if (data.total_points !== undefined) {
            setUser(prev => ({ ...prev, total_points: data.total_points }));
          }
        }
        
        // Open platform share dialog
        openPlatformShare(selectedPlatform);
      } else {
        toast.error(data.message || 'Failed to submit share');
      }
    } catch (err) {
      toast.error('Failed to submit share');
    }
    setSubmitting(false);
  };
  
  // Open platform-specific share with pre-written message
  const openPlatformShare = (platform) => {
    const platformConfig = PLATFORMS.find(p => p.id === platform);
    // Use custom caption if provided, otherwise use platform-specific template
    const shareText = caption || platformConfig?.shareTemplate || `Enjoying this amazing ${selectedItemType || 'moment'} at Bulaccino Cafe! Best cafe in Fiji! Check out their menu at bulaccinocafe.com #BulaccinoCafe #FijiCoffee`;
    const shareUrl = 'https://bulaccinocafe.com';
    
    const urls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`,
      instagram: null, // Instagram doesn't support web sharing, prompt to open app
      google: 'https://g.page/r/CbYHsfEsqm9iEAI/review',
      tripadvisor: 'https://www.tripadvisor.com/Restaurant_Review-g1-d1-Reviews-Bulaccino_Cafe-Fiji.html', // Replace with actual TA link
      whatsapp: `https://wa.me/?text=${encodeURIComponent(shareText + ' ' + shareUrl)}`
    };
    
    const url = urls[platform];
    if (url) {
      window.open(url, '_blank', 'width=600,height=400');
    } else if (platform === 'instagram') {
      // For Instagram, copy the caption and show instructions
      navigator.clipboard?.writeText(shareText);
      toast.info('Caption copied! Open Instagram and create a post with your photo.');
    }
  };
  
  // Get suggested caption based on selected platform
  const getSuggestedCaption = () => {
    if (selectedPlatform) {
      const platformConfig = PLATFORMS.find(p => p.id === selectedPlatform);
      return platformConfig?.shareTemplate || '';
    }
    return '';
  };
  
  // Copy caption to clipboard
  const copyCaption = () => {
    const text = caption || getSuggestedCaption() || `Enjoying this amazing ${selectedItemType || 'moment'} at Bulaccino Cafe! Best cafe in Fiji! Check out their menu at bulaccinocafe.com #BulaccinoCafe #FijiCoffee`;
    navigator.clipboard?.writeText(text);
    toast.success('Caption copied!');
  };
  
  // Loading state
  if (checkingUser) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }
  
  return (
    <>
      <Helmet>
        <title>Share Your Moment | Bulaccino Cafe</title>
        <meta name="description" content="Share your Bulaccino Cafe experience and earn rewards! Snap a photo, share to social media, and earn points for discounts." />
        <meta name="robots" content="noindex" />
      </Helmet>
      
      <div className="min-h-screen bg-gradient-to-b from-background to-muted/30 pb-20 overflow-x-hidden">
        {/* Page Header - reduced padding since no Coffee Pass banner on this page */}
        <div className="pt-20 sm:pt-24 md:pt-28 pb-4 bg-background border-b border-border">
          <div className="max-w-lg mx-auto px-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Camera className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h1 className="font-heading text-lg text-foreground">Share Your Moment</h1>
                <p className="text-xs text-muted-foreground">Earn points for sharing!</p>
              </div>
            </div>
            {user && (
              <div className="text-right">
                <p className="text-xs text-muted-foreground">Points</p>
                <p className="font-bold text-primary">{user.total_points || 0}</p>
              </div>
            )}
          </div>
        </div>
        
        <div className="max-w-lg mx-auto px-4 py-6 space-y-6">
          {/* Live Cafe Stream */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-card border border-border rounded-xl overflow-hidden"
          >
            <div className="p-3 border-b border-border flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
              <span className="text-sm font-medium text-foreground">Live from Bulaccino</span>
            </div>
            <div className="relative aspect-video">
              <iframe 
                width="100%" 
                height="100%" 
                src="https://www.youtube.com/embed/pwJjhEpfWak?si=QSS9aOySgWXp8TO4&controls=0" 
                title="Bulaccino Cafe Live Stream" 
                frameBorder="0" 
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" 
                referrerPolicy="strict-origin-when-cross-origin" 
                allowFullScreen
                className="absolute inset-0"
              />
            </div>
          </motion.div>
          
          {/* User Welcome or Signup Prompt */}
          {user ? (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-primary/10 border border-primary/20 rounded-xl p-4"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                  <User className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="font-medium text-foreground">Hi {user.name?.split(' ')[0] || 'there'}!</p>
                  <p className="text-sm text-muted-foreground">Points will be added to your account</p>
                </div>
              </div>
            </motion.div>
          ) : !showSignup ? (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4 text-center"
            >
              <Sparkles className="w-6 h-6 text-amber-600 mx-auto mb-2" />
              <p className="font-medium text-amber-800 dark:text-amber-300">Earn points for sharing!</p>
              <p className="text-sm text-amber-700 dark:text-amber-400 mb-4">
                Create a FREE Bulaccino Rewards account to claim your points. 200 points = 10% off!
              </p>
              <Button 
                size="sm" 
                onClick={() => setShowSignup(true)}
                className="bg-amber-600 hover:bg-amber-700"
              >
                Create Account <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </motion.div>
          ) : null}
          
          {/* Signup Form */}
          <AnimatePresence>
            {showSignup && !user && (
              <motion.form
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                onSubmit={handleSignup}
                className="bg-card border border-border rounded-xl p-4 space-y-4"
              >
                <div className="flex items-center justify-between">
                  <h3 className="font-heading">Create Account</h3>
                  <button 
                    type="button" 
                    onClick={() => setShowSignup(false)}
                    className="p-1 hover:bg-muted rounded"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                
                <div className="space-y-3">
                  <div>
                    <Label htmlFor="name" className="text-xs">Name</Label>
                    <Input
                      id="name"
                      placeholder="Your name"
                      value={signupData.name}
                      onChange={(e) => setSignupData(prev => ({ ...prev, name: e.target.value }))}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-xs">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="your@email.com"
                      value={signupData.email}
                      onChange={(e) => setSignupData(prev => ({ ...prev, email: e.target.value }))}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-xs">Phone</Label>
                    <Input
                      id="phone"
                      type="tel"
                      placeholder="+679 1234567"
                      value={signupData.phone}
                      onChange={(e) => setSignupData(prev => ({ ...prev, phone: e.target.value }))}
                      className="mt-1"
                    />
                  </div>
                </div>
                
                <Button type="submit" className="w-full" disabled={submitting}>
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Create Account & Earn 50 Points
                </Button>
              </motion.form>
            )}
          </AnimatePresence>
          
          {/* Photo Upload Section */}
          <div className="bg-card border border-border rounded-xl p-4">
            <h3 className="font-heading mb-3 flex items-center gap-2">
              <Camera className="w-4 h-4" />
              Add Your Photo
            </h3>
            
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handlePhotoSelect}
              className="hidden"
            />
            
            {photoPreview ? (
              <div className="relative">
                <img 
                  src={photoPreview} 
                  alt="Preview" 
                  className="w-full h-48 object-cover rounded-lg"
                />
                <button
                  type="button"
                  onClick={() => setPhotoPreview(null)}
                  className="absolute top-2 right-2 p-1 bg-black/50 rounded-full"
                >
                  <X className="w-4 h-4 text-white" />
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-border rounded-lg hover:border-primary/50 transition-colors"
                >
                  <Camera className="w-8 h-8 text-muted-foreground mb-2" />
                  <span className="text-sm">Take Photo</span>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (fileInputRef.current) {
                      fileInputRef.current.removeAttribute('capture');
                      fileInputRef.current.click();
                      fileInputRef.current.setAttribute('capture', 'environment');
                    }
                  }}
                  className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-border rounded-lg hover:border-primary/50 transition-colors"
                >
                  <ImageIcon className="w-8 h-8 text-muted-foreground mb-2" />
                  <span className="text-sm">From Gallery</span>
                </button>
              </div>
            )}
          </div>
          
          {/* Item Type Selection */}
          <div className="bg-card border border-border rounded-xl p-4">
            <h3 className="font-heading mb-3 text-sm">What did you have today?</h3>
            <div className="grid grid-cols-4 gap-2">
              {ITEM_TYPES.map(item => (
                <ItemTypeButton
                  key={item.id}
                  item={item}
                  selected={selectedItemType === item.id}
                  onClick={() => setSelectedItemType(item.id)}
                />
              ))}
            </div>
          </div>
          
          {/* Platform Selection - FIRST so we can show suggested caption */}
          <div className="bg-card border border-border rounded-xl p-4">
            <h3 className="font-heading mb-3 text-foreground">Share to:</h3>
            <div className="grid grid-cols-5 gap-1 sm:gap-2">
              {PLATFORMS.map(platform => (
                <PlatformButton
                  key={platform.id}
                  platform={platform}
                  selected={selectedPlatform === platform.id}
                  onClick={() => setSelectedPlatform(platform.id)}
                />
              ))}
            </div>
          </div>
          
          {/* Suggested Caption - Show when platform is selected */}
          {selectedPlatform && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-card border border-border rounded-xl p-4"
            >
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm font-heading text-foreground">
                  Suggested {PLATFORMS.find(p => p.id === selectedPlatform)?.name} Caption
                </Label>
                <button
                  type="button"
                  onClick={copyCaption}
                  className="text-xs text-primary flex items-center gap-1 hover:underline"
                >
                  <Copy className="w-3 h-3" /> Copy
                </button>
              </div>
              <div className="bg-muted/50 dark:bg-muted/30 rounded-lg p-3 text-sm text-muted-foreground whitespace-pre-wrap">
                {getSuggestedCaption()}
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Tap "Copy" and paste when sharing, or customize below:
              </p>
              <Textarea
                id="caption"
                placeholder="Or write your own caption..."
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                rows={2}
                className="resize-none mt-2 text-foreground"
              />
            </motion.div>
          )}
          
          {/* Points Info */}
          <div className="text-center text-sm text-muted-foreground">
            <p>200 points = 10% off your next visit!</p>
          </div>
          
          {/* Submit Button */}
          <Button
            onClick={handleShare}
            disabled={!selectedPlatform || submitting}
            className="w-full h-14 text-lg"
            size="lg"
          >
            {submitting ? (
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
            ) : shareSuccess ? (
              <CheckCircle className="w-5 h-5 mr-2" />
            ) : null}
            {shareSuccess ? 'Shared! Share Again?' : `Share & Earn ${selectedPlatform ? PLATFORMS.find(p => p.id === selectedPlatform)?.points : ''} Points`}
          </Button>
          
          {/* Review prompts for Google/TripAdvisor */}
          {selectedPlatform === 'google' && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-card border border-border rounded-xl p-4"
            >
              <h4 className="font-heading text-sm mb-2 flex items-center gap-2">
                <Star className="w-4 h-4 text-amber-500" />
                Share Your Experience on Google
              </h4>
              <p className="text-xs text-muted-foreground mb-3">
                Your review helps other coffee lovers find us! Suggested review:
              </p>
              <div className="bg-muted/50 rounded-lg p-3 text-sm mb-3">
                "Great coffee and amazing atmosphere! The {selectedItemType || 'food'} was delicious. 
                Friendly staff and beautiful cafe. Highly recommend! Visit bulaccinocafe.com for their full menu."
              </div>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    navigator.clipboard?.writeText(`Great coffee and amazing atmosphere! The ${selectedItemType || 'food'} was delicious. Friendly staff and beautiful cafe. Highly recommend! Visit bulaccinocafe.com for their full menu.`);
                    toast.success('Review text copied!');
                  }}
                >
                  <Copy className="w-3 h-3 mr-1" /> Copy Text
                </Button>
              </div>
            </motion.div>
          )}
          
          {selectedPlatform === 'tripadvisor' && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-card border border-border rounded-xl p-4"
            >
              <h4 className="font-heading text-sm mb-2 flex items-center gap-2">
                <MapPin className="w-4 h-4 text-green-600" />
                Help Travelers Discover Bulaccino
              </h4>
              <p className="text-xs text-muted-foreground mb-3">
                Tourists planning their Fiji trip read TripAdvisor! Suggested review:
              </p>
              <div className="bg-muted/50 rounded-lg p-3 text-sm mb-3">
                "A must-visit cafe in Fiji! Excellent coffee, fresh food, and wonderful service. 
                Check out their Bula Coffee Club for amazing value! bulaccinocafe.com"
              </div>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    navigator.clipboard?.writeText(`A must-visit cafe in Fiji! Excellent coffee, fresh food, and wonderful service. Check out their Bula Coffee Club for amazing value! bulaccinocafe.com`);
                    toast.success('Review text copied!');
                  }}
                >
                  <Copy className="w-3 h-3 mr-1" /> Copy Text
                </Button>
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </>
  );
}
